import { useEffect, useState } from 'react'
import { CopyIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopDialog,
    ScoopTextField,
    ScoopText,
    ScoopColumn,
    ScoopRow,
    useToast,
} from '../../../../ui'
import { useSelector } from 'react-redux'
import { useApi } from '../../../../../api/api'
import { SelectType } from '../SelectType/SelectType'
import { EVENTS, trackEvent } from '../../../../../events'
import styles from './EmailForward.module.scss'

export const EmailForward = ({ onClose, stepBack, getMetadata, workspaceMetadata }) => {
    const toast = useToast()
    const [emailGenerated, setEmailGenerated] = useState('')
    const [step, setStep] = useState(0)
    const [reportName, setReportName] = useState('')
    const [reportDescription, setReportDescription] = useState('')
    const [type, setType] = useState('snapshot')
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const { postData } = useApi(
        'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/inboxemailactions'
    )

    const [basicConfiguration, setBasicConfiguration] = useState(null)

    useEffect(() => {
        if (step === 0) {
            setEmailGenerated(`${generateUUID()}@scoopup.report`)
        }
    }, [step])
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0
            const v = c === 'x' ? r : (r & 0x3) | 0x8
            return v.toString(16)
        })
    }

    const generateInboxName = (emailGenerated) => {
        // Remove the exact substring "@scoopup.report"
        let withoutDomain = emailGenerated.replace(/@scoopup\.report/g, '')

        // Replace non-alphanumeric characters, excluding dashes, with an underscore
        let result = withoutDomain.replace(/[^a-zA-Z0-9\-]/g, '_')

        return result
    }

    const handleSaveBasicConfiguration = async (basicConfiguration) => {
        setBasicConfiguration(basicConfiguration)
        setStep(2)
    }

    const handleCreateDataset = async () => {
        toast.info('Creating Dataset, please wait')
        const action = {
            action: 'createInbox',
            inboxName: generateInboxName(emailGenerated),
            label: reportName,
            description: reportDescription,
            workspaceID: workspaceID,
            keepOnlyCurrent: basicConfiguration.isMostRecent,
            incremental: basicConfiguration.isIncremental,
            enableMultipleSameDayLoads: basicConfiguration.isMultipleLoads,
            isTransactional: type.toLowerCase() === 'transactional',
            inboxRange: basicConfiguration.workbookRange,
            userName: userID,
            userID: userID,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        const result = await postData(action)
        trackEvent(EVENTS.DATASET.CONNECT_EMAIL_ATTEMPT, {
            inbox: { name: reportName },
        })
        if (result.status === 'success') {
            toast.success('Dataset Created Successfully')
            getMetadata()
            trackEvent(EVENTS.DATASET.CONNECT_EMAIL_COMPLETE, {
                inbox: {
                    name: reportName,
                    inboxID: result?.inboxID,
                },
            })
        }
        onClose()
        return result
    }
    return (
        <>
            {step === 1 ? (
                <SelectType
                    handleContinue={handleSaveBasicConfiguration}
                    index={1}
                    setType={setType}
                    step={step}
                    setStep={setStep}
                    onClose={onClose}
                    continueButton={'Continue'}
                />
            ) : (
                <ScoopDialog
                    style={{ minWidth: '500px' }}
                    title={'Dataset from Email'}
                    onClose={onClose}
                    open
                    actions={
                        <>
                            {step === 0 ? (
                                <>
                                    <ScoopButton variant="grey" onClick={stepBack}>
                                        Back
                                    </ScoopButton>
                                    <ScoopButton
                                        onClick={() => {
                                            if (
                                                workspaceMetadata?.account?.accountType?.startsWith(
                                                    'appsumo'
                                                )
                                            ) {
                                                setType('Transactional')
                                                setStep(2) // Skip step 1 (SelectType dialog)
                                            } else {
                                                setStep(1)
                                            }
                                        }}
                                    >
                                        Let's go
                                    </ScoopButton>
                                </>
                            ) : (
                                <>
                                    <ScoopButton variant="grey" onClick={() => setStep(1)}>
                                        Back
                                    </ScoopButton>
                                    <ScoopButton
                                        onClick={() => {
                                            handleCreateDataset()
                                        }}
                                    >
                                        Create Dataset
                                    </ScoopButton>
                                </>
                            )}
                        </>
                    }
                >
                    {step === 0 ? (
                        <ScoopColumn className={styles.step0Container}>
                            <ScoopText className={styles.descriptionText}>
                                Scoop data from any email containing a data attachment.
                                <br />
                                Follow this step by step guide or{' '}
                                <a
                                    href="javascript:void(0);"
                                    className={styles.helpLink}
                                    onClick={() => {
                                        trackEvent(EVENTS.ACCOUNT.CONTACT_SUPPORT, {
                                            source: 'email_forward_help',
                                        })
                                        window.open('https://scoopanalytics.com/help', '_blank')
                                    }}
                                >
                                    read more here
                                </a>
                            </ScoopText>

                            <div className={styles.verticalStepper}>
                                <div className={styles.stepItem}>
                                    <div className={styles.stepIconContainer}>
                                        <div className={styles.stepIcon}>1</div>
                                        <div className={styles.stepConnector} />
                                    </div>
                                    <ScoopText className={styles.stepLabel}>
                                        Tell us whether your data is transactional or a snapshot
                                    </ScoopText>
                                </div>
                                <div className={styles.stepItem}>
                                    <div className={styles.stepIconContainer}>
                                        <div className={styles.stepIcon}>2</div>
                                        <div className={styles.stepConnector} />
                                    </div>
                                    <ScoopText className={styles.stepLabel}>
                                        Visit your inbox and find the email you want to Scoop data
                                        from
                                    </ScoopText>
                                </div>
                                <div className={styles.stepItem}>
                                    <div className={styles.stepIconContainer}>
                                        <div className={styles.stepIcon}>3</div>
                                    </div>
                                    <ScoopText className={styles.stepLabel}>
                                        Forward that email to an inbox email address we'll provide
                                    </ScoopText>
                                </div>
                            </div>
                        </ScoopColumn>
                    ) : (
                        <ScoopColumn className={styles.step2Container}>
                            <ScoopTextField
                                label={'Name'}
                                placeholder={'My data file'}
                                value={reportName}
                                onChange={(e) => setReportName(e.target.value)}
                            />
                            <ScoopTextField
                                label={'Description'}
                                placeholder={'Optional'}
                                value={reportDescription}
                                onChange={(e) => setReportDescription(e.target.value)}
                            />

                            <ScoopText className={styles.emailLabelText}>
                                Copy this email address and forward your email here once done
                            </ScoopText>

                            <ScoopRow
                                className={styles.emailDisplayBox}
                                id="email-subject-textfield"
                            >
                                <ScoopText>{emailGenerated}</ScoopText>
                                <div
                                    className={styles.copyIconButton}
                                    onClick={() => {
                                        navigator.clipboard.writeText(emailGenerated)
                                        toast.success('Email copied to clipboard')
                                    }}
                                >
                                    <CopyIcon size={20} />
                                </div>
                            </ScoopRow>
                        </ScoopColumn>
                    )}
                </ScoopDialog>
            )}
        </>
    )
}
